import React from 'react'
import jjv from 'jjv'

const TOUCHED_TRUE_PROPAGATE = 'touchedTruePropagate'
const TOUCHED_FALSE_PROPAGATE = 'touchedFalsePropagate'
const TOUCHED_TRUE = 'touchedTrue'
const TOUCHED_FALSE = 'touchedFalse'

export function nodeIsValid(...nodes) {
    return nodes.find((node) => {
        return !!node && !node.isValid()
    }) == null
}

export function nodeHasValue(...nodes) {
    return nodes.find((node) => {
        const value = node && node().value

        if (value == null) {
            return false
        } else if (value instanceof Array) {
            return value.length > 0
        }
        return true
    }) != null
}

export function checkboxField(field) {
    const checked = !!field.value

    function onChange() {
        field.onChange(!checked)
    }

    return {...field, checked, onChange}
}

export function buttonField(field, value) {
    const active = field.value == value

    function onClick(event) {
        if (event) {
            event.preventDefault()
        }
        field.onChange(value)
    }

    return {active, onClick}
}

export function selectField(field, multi) {
    var value
    var onChange

    if (multi) {
        value = field.value

        onChange = (event) => {
            const options = [...event.target.options]
            const nextValue = options.filter((o) => o.selected).map((o) => o.value)
            field.onChange(nextValue)
        }
    } else {
        value = field.value || ''
        onChange = field.onChange
    }

    return {...field, value, onChange}
}

export function arrayItemField(field) {
    const arrayValue = field.value || []
    const value = arrayValue[0]

    function onChange(eventOrValue) {
        var nextValue
        if (eventOrValue && eventOrValue.target) {
            nextValue = eventOrValue.target.value
        } else {
            nextValue = eventOrValue
        }

        var nextItemValue
        if (nextValue instanceof Date) {
            nextItemValue = nextValue
        } else if (nextValue && typeof nextValue == 'object') {
            nextItemValue = {...arrayValue[0], ...nextValue}
        } else {
            nextItemValue = nextValue
        }

        var nextArrayValue = [...arrayValue]
        nextArrayValue[0] = nextItemValue || null
        if (nextArrayValue.length == 1 && !nextArrayValue[0]) {
            // reset array
            nextArrayValue = []
        }

        field.onChange(nextArrayValue)
    }

    return {...field, value, onChange}
}

export function addArrayItemField(field) {
    const arrayValue = field.value || []
    const value = null

    function onChange(value) {
        const nextValue = [value, ...arrayValue]
        field.onChange(nextValue)
    }

    return {...field, value, onChange}
}

export function updateField(field, originalValue) {
    var value = field.value
    if (field.value === undefined) {
        value = originalValue
    }

    function onChange(event) {
        const value = event.target.value || null

        var nextValue = value
        if (value == originalValue) {
            nextValue = undefined
        }
        field.onChange(nextValue)
    }

    return {...field, value, onChange}
}

export function integerField(field) {
    function onChange(eventOrValue) {
        var nextValue
        if (eventOrValue && eventOrValue.target) {
            nextValue = eventOrValue.target.value
        } else {
            nextValue = eventOrValue
        }

        field.onChange(parseInt(nextValue))
    }

    return {...field, onChange}
}

export function percentageField(field) {
    const value = field.value && Math.round(field.value * 100)

    function onChange(eventOrValue) {
        var nextValue
        if (eventOrValue && eventOrValue.target) {
            nextValue = eventOrValue.target.value
        } else {
            nextValue = eventOrValue
        }

        field.onChange(parseInt(nextValue) / 100)
    }

    return {...field, value, onChange}
}

export function controlledField(field, defaultValue = '') {
    // workaround when passing null value to react input text area
    // this prevent the component to become uncontrolled
    const value = field.value || defaultValue
    return {...field, value}
}

function filter(object, keys, allways) {
    if (keys && (object || allways)) {
        return keys.reduce((r, v) => {
            r[v] = object && object[v]
            return r
        }, {})
    }
    return object
}

function includes(source, target) {
    source = (source instanceof Array) && source || [source]
    target = (target instanceof Array) && target || [target]

    for (var i = 0 ; i < target.length ; i++) {
        if (source.indexOf(target[i]) < 0) return false
    }
    return true
}

function empty(object) {
    return object && Object.keys(object).findIndex((k) => object[k] != null) < 0
}

function sanitize(schema, value) {
    if (includes(schema.type, ['object', 'null'])) {
        if (empty(value)) return null
    }

    if (value === 0 || value === false || value === undefined) return value
    return value || null
}

function sanitizeValidation(value) {
    if (value === undefined) return null
    return value
}

function getTouched(touched, schema, target, property) {
    if (includes(schema.type, ['object', 'null'])) {
        if (!(target && target[property])) return TOUCHED_FALSE_PROPAGATE
    }

    if (touched == TOUCHED_TRUE_PROPAGATE) return touched
    if (touched == TOUCHED_FALSE_PROPAGATE) return touched
    return (target && target[property] !== undefined) && TOUCHED_TRUE || TOUCHED_FALSE
}

function isTouched(touched) {
    return touched == TOUCHED_TRUE_PROPAGATE || touched == TOUCHED_TRUE
}

function connectForm(options) {
    const formName = options.name || 'form'
    const targetName = options.target
    const targetAlias = options.alias || targetName
    const selectSchema = (typeof options.schema == 'function') && options.schema || (() => options.schema)
    const actionType = options.action
    const validator = options.validator || jjv()

    return (WrappedComponent) => {

        class FormConnector extends React.Component {

            constructor(props) {
                super(props)

                const nextTarget = props[targetName]
                const schema = selectSchema(nextTarget, props)
                const touched = TOUCHED_FALSE
                const nextForm = this._computeNextForm(schema, null, touched, nextTarget)

                this._schema = schema
                this._touched = touched

                this.state = {
                    [formName]: nextForm
                }
            }

            componentWillReceiveProps(nextProps) {
                const nextTarget = nextProps[targetName]
                const target = this.props[targetName]
                const {form} = this.state

                const schema = selectSchema(nextTarget, nextProps)
                const nextForm = this._computeNextForm(schema, this._schema == schema && form || null, this._touched, nextTarget, target)

                this._schema = schema

                this.setState({
                    [formName]: nextForm
                })
            }

            _computeNextForm(schema, form, touched, nextTarget, target) {
                if (!form || nextTarget !== target) {
                    const nextForm = this._createForm(schema, nextTarget)
                    
                    for (var property in schema.properties) {
                        const propSchema = schema.properties[property]
                        const propRoot = form && form[property]
                        const propNextTarget = (nextTarget || undefined) && nextTarget[property]
                        const propTarget = (target || undefined) && target[property]
                        const propTouched = getTouched(touched, propSchema, nextTarget, property)

                        nextForm[property] = this._computeNextNode(property, propTouched, propSchema, propRoot, propNextTarget, propTarget, (value) => {
                            const {dispatch} = this.props
                            dispatch({
                                type: actionType,
                                [targetAlias]: value
                            })
                        })
                    }
                    return nextForm
                }
                return form
            }

            _createForm(schema, value) {
                const errors = validator.validate(schema, sanitizeValidation(value))

                const form = (fields) => {
                    const fieldsValue = filter(value, fields)

                    return {
                        value: fieldsValue,
                        onChange: (eventOrValue) => {
                            const {dispatch} = this.props

                            var nextFieldsValue
                            if (eventOrValue && eventOrValue.target) {
                                nextFieldsValue = eventOrValue.target.value
                            } else {
                                nextFieldsValue = eventOrValue
                            }
                            nextFieldsValue = filter(nextFieldsValue, fields, true)

                            dispatch({
                                type: actionType,
                                [targetAlias]: sanitize(schema, nextFieldsValue)
                            })
                        }
                    }
                }

                form.isValid = () => {
                    return errors == null
                }

                form.validate = () => {
                    const nextTarget = this.props[targetName]

                    const touched = (errors == null) && TOUCHED_FALSE || TOUCHED_TRUE_PROPAGATE

                    if (this._touched != touched) {
                        // show / hide errors
                        const nextForm = this._computeNextForm(this._schema, null, touched, nextTarget)

                        this._touched = touched

                        this.setState({
                            [formName]: nextForm
                        })
                    }

                    return errors == null
                }

                return form
            }

            _computeNextNode(property, touched, schema, node, nextTarget, target, onChange) {
                if (!node || nextTarget !== target) {
                    const nextNode = this._createNode(property, touched, schema, nextTarget)

                    for (var prop in schema.properties) {
                        const propSchema = schema.properties[prop]
                        const propNode = node && node[prop]
                        const propNextTarget = (nextTarget || undefined) && nextTarget[prop]
                        const propTarget = (target || undefined) && target[prop]
                        const propTouched = getTouched(touched, propSchema, nextTarget, prop)

                        nextNode[prop] = this._computeNextNode(prop, propTouched, propSchema, propNode, propNextTarget, propTarget, (value) => {
                            const nextValue = {...nextTarget, ...value}
                            nextNode._onChange({[property]: sanitize(schema, nextValue)})
                        })
                    }

                    if (schema.items) {
                        for (var prop in schema.items.properties) {
                            const propSchema = schema.items.properties[prop]
                            const propNode = node && node[prop]
                            const propNextTarget = (nextTarget || undefined) && (nextTarget[0] || undefined) && nextTarget[0][prop]
                            const propTarget = (target || undefined) && (target[0] || undefined) && target[0][prop]
                            const propTouched = getTouched(touched, propSchema, nextTarget && nextTarget[0], prop)

                            nextNode[prop] = this._computeNextNode(prop, propTouched, propSchema, propNode, propNextTarget, propTarget, (value) => {
                                const arrayValue = nextTarget || []
                                const nextItemValue = {...arrayValue[0], ...value}

                                var nextArrayValue = [...arrayValue]
                                nextArrayValue[0] = nextItemValue
                                nextNode._onChange({[property]: sanitize(schema, nextArrayValue)})
                            })
                        }
                    }

                    nextNode._onChange = onChange
                    return nextNode
                }

                node._onChange = onChange
                return node
            }

            _createNode(property, touched, schema, value) {
                const errors = validator.validate(schema, sanitizeValidation(value))
                var field

                const node = () => {
                    return field || (field = {
                        value: value,
                        bsStyle: isTouched(touched) && errors && 'error' || null,
                        onChange: (eventOrValue) => {
                            var nextValue
                            if (eventOrValue && eventOrValue.target) {
                                nextValue = eventOrValue.target.value
                            } else {
                                nextValue = eventOrValue
                            }

                            node._onChange({[property]: sanitize(schema, nextValue)})
                        }
                    })
                }

                if (schema.items) {
                    node.add = (event, item = null) => {
                        if (event) {
                            event.preventDefault()
                        }

                        const arrayValue = value || []
                        const nextArrayValue = [item, ...arrayValue]

                        node._onChange({[property]: sanitize(schema, nextArrayValue)})
                    }

                    node.remove = (event, item) => {
                        if (event) {
                            event.preventDefault()
                        }

                        const arrayValue = value || []
                        const index = item && arrayValue.indexOf(item) || 0
                        if (index >= 0) {
                            var nextArrayValue = [...arrayValue]
                            nextArrayValue.splice(index, 1)

                            if (nextArrayValue.length == 1 && !nextArrayValue[0]) {
                                // reset array
                                nextArrayValue = []
                            }

                            node._onChange({[property]: sanitize(schema, nextArrayValue)})
                        }
                    }

                    node.size = () => {
                        const arrayValue = value || []
                        return arrayValue.length
                    }
                }


                node.isRequired = () => {
                    return !(schema.type && (schema.type instanceof Array) && schema.type.indexOf('null') >= 0)
                }

                node.isValid = () => {
                    return errors == null
                }

                return node
            }

            getWrappedInstance() {
                const {wrappedInstance} = this.refs
                return wrappedInstance
            }

            render() {
                return <WrappedComponent ref='wrappedInstance' {...this.props} {...this.state} />
            }
        }

        return FormConnector
    }
}

export {connectForm}


// WEBPACK FOOTER //
// ./lib/core/redux/form.js